/**
 * Dashboard materiaal roll-up for the "Alles" view (Fase 6c).
 *
 * Project-wide materiaal across EVERY fase: a total header (materiaallijsten +
 * fase count) followed by one COLLAPSIBLE MateriaalFaseGroup per fase (collapsed
 * by default). Each fase group is scoped via the SAME `buildActiveFaseGroup`
 * merge as DashboardMateriaalSection — a legacy materiaallijst whose
 * `x_studio_projecttaak` points at a CHILD task surfaces under its parent fase.
 *
 * Full per-fase parity with DashboardMateriaalSection: upload / CAD / delete /
 * opgelijst, gated on `csv_upload`, reading the same store slices + the shared
 * `useMateriaalFaseControls` hook + `MateriaalCadModals`, with the shared
 * uploadError banner.
 */
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { ProjectTask } from '@/types/odoo';
import type { DashboardFase } from '@/api/projectFases';
import { useProjectViewStore } from '@/stores/projectView';
import { usePermissionsStore } from '@/stores/permissions';
import { buildFaseGroups, buildParentByChild, materiaalTotals, normalizeFaseId, type FaseGroup } from '@/utils/materiaal';
import { MateriaalFaseGroup } from '@/components/project/materiaal/MateriaalFaseGroup';
import { MateriaalCadModals } from '@/components/project/materiaal/MateriaalCadModals';
import { useMateriaalFaseControls } from '@/hooks/useMateriaalFaseControls';
import { buildActiveFaseGroup } from './DashboardMateriaalSection';

interface DashboardMateriaalAllesProps {
  projectId: number;
  projectTasks: ProjectTask[];
  /** The project's fases (parent planning tasks) — one group is rendered per fase. */
  fases: DashboardFase[];
  /** True while the materiaal store slice is still loading for this project. */
  loading?: boolean;
  /** Materiaal-fetch error message, if any. */
  error?: string | null;
  /** Rendered inside a DashboardSection coloured shell — drop our own outer card
   *  chrome and the duplicated "Materiaal" title (the shell owns both). The
   *  materiaallijst-count caption + drag tip stay (genuine sub-context). */
  embedded?: boolean;
}

export function DashboardMateriaalAlles({
  projectId,
  projectTasks,
  fases,
  loading = false,
  error = null,
  embedded = false,
}: DashboardMateriaalAllesProps) {
  const { materiaalLines, materiaalPickings, materiaalMoves, uploadError } = useProjectViewStore(
    useShallow((s) => ({
      materiaalLines: s.materiaalLines,
      materiaalPickings: s.materiaalPickings,
      materiaalMoves: s.materiaalMoves,
      uploadError: s.uploadError,
    })),
  );

  // `uploadError` is a shared store slice. Clear any stale message when entering
  // the roll-up or switching project, so an error from another view doesn't bleed in.
  useEffect(() => {
    useProjectViewStore.setState({ uploadError: null });
  }, [projectId]);

  // Gate writes on csv_upload (today the dashboard is admin-only, so this is a
  // no-op; it prevents over-exposure should the tab later reach a read-only role).
  const canUpload = usePermissionsStore((s) => s.canFeature)('csv_upload');

  const controls = useMateriaalFaseControls(projectId, projectTasks);

  // One scoped FaseGroup per fase (parent-or-self merge), in fase order, PLUS an
  // "Overig" group for materiaallijsten not attributable to any fase (no
  // x_studio_projecttaak / orphan lines / a task absent from the server fase list).
  // The roll-up is the project TOTAL, so these must be shown + counted, not dropped.
  const { faseGroups, overigGroup } = useMemo(() => {
    const allGroups = buildFaseGroups(
      materiaalPickings ?? [],
      materiaalLines ?? [],
      projectTasks,
      materiaalMoves ?? [],
    );
    const perFase = fases.map((fase) => buildActiveFaseGroup(allGroups, projectTasks, fase.id));

    const parentByChild = buildParentByChild(projectTasks);
    const faseIdSet = new Set(fases.map((f) => f.id));
    const leftoverPickings = allGroups
      .filter((g) => !faseIdSet.has(normalizeFaseId(g.taskId, parentByChild)))
      .flatMap((g) => g.pickingGroups)
      .sort((a, b) => a.picking.name.localeCompare(b.picking.name));
    const overig: FaseGroup | null = leftoverPickings.length > 0
      ? { taskId: 0, taskName: 'Niet aan een fase gekoppeld', pickingGroups: leftoverPickings }
      : null;

    return { faseGroups: perFase, overigGroup: overig };
  }, [materiaalPickings, materiaalLines, materiaalMoves, projectTasks, fases]);

  const totals = useMemo(
    () => materiaalTotals(overigGroup ? [...faseGroups, overigGroup] : faseGroups),
    [faseGroups, overigGroup],
  );

  // The store slices are null until the first fetch resolves; treat that as
  // loading so we don't flash an empty roll-up before pickings arrive.
  const stillLoading = loading || materiaalPickings === null;

  const Wrapper = embedded ? 'div' : 'section';
  return (
    <Wrapper
      className={embedded ? '' : 'rounded-xl border border-gray-200 bg-white p-4 sm:p-5'}
      aria-label="Materiaal"
    >
      <div className="mb-4">
        {!embedded && <h2 className="text-base font-extrabold text-td-navy">Materiaal</h2>}
        <p className="text-xs text-td-muted mt-0.5">
          {totals.totalPickings} {totals.totalPickings === 1 ? 'materiaallijst' : 'materiaallijsten'} in{' '}
          {fases.length} {fases.length === 1 ? 'fase' : 'fases'}
        </p>
        {canUpload && (
          <p className="hidden sm:block text-xs text-td-muted mt-1">
            Tip: sleep een CSV-bestand op een fase om te uploaden.
          </p>
        )}
      </div>

      {/* Upload / delete / invalid-drop failures land in the shared uploadError
          slice — surface them here (parity with DashboardMateriaalSection). */}
      {uploadError && (
        <div className="mb-3 bg-red-50 text-red-600 p-3 rounded-xl flex items-start gap-3" role="alert">
          <div className="flex-1">
            <p className="font-semibold text-sm">Er ging iets mis</p>
            <p className="text-sm mt-0.5">{uploadError}</p>
          </div>
          <button
            type="button"
            onClick={() => useProjectViewStore.setState({ uploadError: null })}
            className="shrink-0 text-red-400 hover:text-red-600 transition-colors"
            aria-label="Foutmelding sluiten"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {error ? (
        <p className="text-sm text-td-magenta py-6 text-center">Materiaal laden mislukt: {error}</p>
      ) : stillLoading ? (
        <p className="text-sm text-td-muted py-6 text-center">Materiaal laden…</p>
      ) : faseGroups.length === 0 && !overigGroup ? (
        <p className="text-sm text-td-muted py-6 text-center">Geen fases in dit project.</p>
      ) : (
        <div className="space-y-2">
          {faseGroups.map((group) => {
            // Children of this fase — drives the per-child opgelijst rows.
            const childTasks = projectTasks
              .filter((t) => t.parent_id && t.parent_id[0] === group.taskId)
              .sort((a, b) => {
                if (!a.date_deadline && !b.date_deadline) return 0;
                if (!a.date_deadline) return 1;
                if (!b.date_deadline) return -1;
                return a.date_deadline.localeCompare(b.date_deadline);
              });
            return (
              <MateriaalFaseGroup
                key={group.taskId}
                fase={group}
                defaultOpen={false}
                isUploading={controls.isUploading}
                isEasykit={false}
                childTasks={childTasks}
                getOpgelijstValue={controls.getOpgelijstValue}
                onToggleOpgelijst={controls.onToggleOpgelijst}
                onUpload={async (file) => {
                  await controls.onUpload(group.taskId, file);
                }}
                onCadClick={() => {
                  controls.cad.openCad({ taskId: group.taskId, taskName: group.taskName });
                }}
                onDelete={controls.onDelete}
                onError={controls.onError}
                allowUpload={canUpload}
              />
            );
          })}

          {/* Materiaallijsten not attributable to a fase — shown so the roll-up is the
              true project total. Read-only (no fase to upload/opgelijst to); delete stays
              for cleaning up orphan lijsten. */}
          {overigGroup && (
            <MateriaalFaseGroup
              fase={overigGroup}
              defaultOpen={false}
              isUploading={controls.isUploading}
              isEasykit={false}
              childTasks={[]}
              getOpgelijstValue={controls.getOpgelijstValue}
              onToggleOpgelijst={controls.onToggleOpgelijst}
              onUpload={async () => {}}
              onCadClick={() => {}}
              onDelete={controls.onDelete}
              onError={controls.onError}
              allowUpload={false}
              allowDelete={canUpload}
              allowOpgelijst={false}
              allowCad={false}
            />
          )}
        </div>
      )}

      <MateriaalCadModals controls={controls} projectId={projectId} />
    </Wrapper>
  );
}
